import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChunkedText, generateThread, clearChunks } from './textChunkerSlice';
import {
  Box,
  TextField,
  Typography,
  Fade,
  CircularProgress,
  useTheme,
  Container,
  Stack,
  Alert,
  Grid,
  Button,
  alpha,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  ContentCut,
  AutoAwesome,
  Refresh,
  ContentCopy,
  Twitter,
  LinkedIn,
  Instagram,
  ViewCarousel,
  ViewAgenda,
  HistoryEdu,
  RocketLaunch,
  Psychology,
  School,
  Share,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../theme/ThemeProvider';
import { useAnalytics } from '../../components/Analytics';
import AffiliateBox from '../../components/Blog/AffiliateBox';

const PLATFORMS = {
  twitter: { name: 'Twitter / X', icon: <Twitter />, color: '#1DA1F2' },
  linkedin: { name: 'LinkedIn', icon: <LinkedIn />, color: '#0077B5' },
  instagram: { name: 'Instagram', icon: <Instagram />, color: '#E4405F' },
};

const PostSplitter = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { darkMode } = useCustomTheme();

  const { chunks, loading, error } = useSelector(state => state.textChunker);

  const [activeTab, setActiveTab] = useState(0); // 0: Smart Remix, 1: Classic Split
  const [inputText, setInputText] = useState('');

  // Smart Remix State
  const [platform, setPlatform] = useState('twitter');
  const [tone, setTone] = useState('viral');
  const [format, setFormat] = useState('thread');

  // Classic Split State
  const [splitMethod, setSplitMethod] = useState('chars');

  const { trackToolUsage, trackTimeSpent } = useAnalytics();
  const [toolOpenTime] = useState(Date.now());

  useEffect(() => {
    trackToolUsage('repurposing_studio');
    return () => {
      trackTimeSpent('repurposing_studio', Math.round((Date.now() - toolOpenTime) / 1000));
    };
  }, []);

  const handleReset = () => {
    setInputText('');
    dispatch(clearChunks());
  };

  const handleSmartSubmit = () => {
    if (!inputText.trim()) return;
    dispatch(generateThread({ text: inputText, platform, tone, format }));
  };

  const handleClassicSubmit = () => {
    if (!inputText.trim()) return;
    const maxChars = platform === 'twitter' ? 280 : platform === 'linkedin' ? 1300 : 2200;
    dispatch(fetchChunkedText({
      text: inputText,
      filters: { chunkSize: maxChars, preserveLineBreaks: true }
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: darkMode ? '#0a0a0a' : '#f8fafc', pb: 10 }}>
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Fade in timeout={800}>
            <Box sx={{ display: 'inline-flex', mb: 3 }}>
              <Box sx={{
                p: 2,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                boxShadow: '0 8px 32px rgba(6, 182, 212, 0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <ViewCarousel sx={{ fontSize: 48, color: 'white' }} />
              </Box>
            </Box>
          </Fade>
          <Typography variant="h1" sx={{
            fontSize: { xs: '2.5rem', md: '4rem' },
            fontWeight: 900,
            mb: 2,
            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}>
            Repurposing Studio
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto', fontWeight: 500, opacity: 0.8 }}>
            Transform generic text into viral threads, carousels, and optimized social posts in seconds.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Control Panel */}
          <Grid item xs={12} lg={4}>
            <Card sx={{
              height: '100%',
              borderRadius: '32px',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: darkMode ? alpha('#1f2937', 0.6) : alpha('#fff', 0.8),
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
            }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  sx={{ mb: 4, '& .MuiTabs-indicator': { height: 3, borderRadius: 3 } }}
                  centered
                >
                  <Tab label="Smart Remix (AI)" icon={<AutoAwesome />} iconPosition="start" />
                  <Tab label="Classic Split" icon={<ContentCut />} iconPosition="start" />
                </Tabs>

                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={activeTab === 0 ? "Paste your rough draft, article, or thoughts here..." : "Paste text to split strictly by character count..."}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      bgcolor: darkMode ? alpha('#000', 0.2) : alpha('#fff', 0.5)
                    }
                  }}
                />

                {activeTab === 0 ? (
                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>Target Platform</InputLabel>
                      <Select
                        value={platform}
                        label="Target Platform"
                        onChange={(e) => setPlatform(e.target.value)}
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value="twitter"><Box sx={{ display: 'flex', gap: 1 }}><Twitter fontSize="small" /> Twitter / X Thread</Box></MenuItem>
                        <MenuItem value="linkedin"><Box sx={{ display: 'flex', gap: 1 }}><LinkedIn fontSize="small" /> LinkedIn Carousel</Box></MenuItem>
                        <MenuItem value="instagram"><Box sx={{ display: 'flex', gap: 1 }}><Instagram fontSize="small" /> Instagram Swipe</Box></MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Tone of Voice</InputLabel>
                      <Select
                        value={tone}
                        label="Tone of Voice"
                        onChange={(e) => setTone(e.target.value)}
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value="viral"><Box sx={{ display: 'flex', gap: 1 }}><RocketLaunch fontSize="small" /> Viral / Hook-Driven</Box></MenuItem>
                        <MenuItem value="professional"><Box sx={{ display: 'flex', gap: 1 }}><Psychology fontSize="small" /> Professional / Insightful</Box></MenuItem>
                        <MenuItem value="story"><Box sx={{ display: 'flex', gap: 1 }}><HistoryEdu fontSize="small" /> Storytelling / Narrative</Box></MenuItem>
                        <MenuItem value="educational"><Box sx={{ display: 'flex', gap: 1 }}><School fontSize="small" /> Educational / How-To</Box></MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={handleSmartSubmit}
                      disabled={!inputText.trim() || loading}
                      startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <AutoAwesome />}
                      sx={{
                        py: 2, borderRadius: '16px', fontSize: '1.1rem', fontWeight: 800,
                        background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
                      }}
                    >
                      {loading ? 'Remixing...' : 'Generate Content'}
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>Split For</InputLabel>
                      <Select
                        value={platform}
                        label="Split For"
                        onChange={(e) => setPlatform(e.target.value)}
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value="twitter">Twitter (280 chars)</MenuItem>
                        <MenuItem value="linkedin">LinkedIn (1300 chars)</MenuItem>
                        <MenuItem value="instagram">Instagram (2200 chars)</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      fullWidth
                      size="large"
                      variant="outlined"
                      onClick={handleClassicSubmit}
                      disabled={!inputText.trim() || loading}
                      startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <ContentCut />}
                      sx={{ py: 2, borderRadius: '16px', fontSize: '1.1rem', fontWeight: 700, borderWidth: 2 }}
                    >
                      Split Text
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Results Display */}
          <Grid item xs={12} lg={8}>
            {chunks.length > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>
                    {activeTab === 0 ? 'Remixed Content' : 'Split Segments'}
                  </Typography>
                  <Button size="small" startIcon={<Refresh />} onClick={handleReset}>Clear All</Button>
                </Box>

                <Box sx={{ position: 'relative' }}>
                  {/* Connector Line for Thread View */}
                  {platform === 'twitter' && (
                    <Box sx={{
                      position: 'absolute', left: 32, top: 40, bottom: 40, width: 2,
                      bgcolor: alpha(theme.palette.divider, 0.5), zIndex: 0
                    }} />
                  )}

                  <Stack spacing={3}>
                    {chunks.map((chunk, i) => (
                      <Fade in timeout={500 + (i * 100)} key={i}>
                        <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 1 }}>
                          {/* Avatar Area */}
                          <Box sx={{ flexShrink: 0, pt: 1 }}>
                            <Box sx={{
                              width: 64, height: 64, borderRadius: '50%',
                              bgcolor: PLATFORMS[platform]?.color || 'primary.main',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              border: `4px solid ${darkMode ? '#0a0a0a' : '#f8fafc'}`
                            }}>
                              {i + 1}
                            </Box>
                          </Box>

                          {/* Content Card */}
                          <Paper sx={{
                            flexGrow: 1, p: 3, borderRadius: '24px', position: 'relative',
                            bgcolor: darkMode ? alpha('#1f2937', 0.8) : 'white',
                            border: `1px solid ${theme.palette.divider}`,
                          }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', mb: 2 }}>
                              {chunk}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                              <Typography variant="caption" color="text.secondary">
                                {chunk.length} chars
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Copy Tweet">
                                  <IconButton size="small" onClick={() => copyToClipboard(chunk)}>
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {platform === 'twitter' && (
                                  <Tooltip title="Share on X">
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(chunk)}`, '_blank')}
                                    >
                                      <Share fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        </Box>
                      </Fade>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ mt: 6 }}>
                  <AffiliateBox context="Social Media Growth" />
                </Box>
              </Box>
            ) : (
              <Box sx={{
                height: '100%',
                minHeight: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.4,
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: '32px',
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}>
                <ViewCarousel sx={{ fontSize: 80, mb: 2, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">Ready to Repurpose?</Typography>
                <Typography variant="body2" color="text.secondary">Select "Smart Remix" to use AI or "Classic Split" for manual control.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PostSplitter;
